// Core Types for TalentKey.AI Application

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  country_code?: string;
  phone?: string;
  avatar_url?: string;
  job_title?: string;
  company_id?: string;
  role: UserRole;
  is_super_admin: boolean;
  super_admin_role?: SuperAdminRole;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  full_name: string;
  company?: Company;
  reports_to_id?: string;
  reports_to_name?: string;
  send_email?: boolean;
  persona?: string; // "job_seeker", "company_user", "super_admin", "legacy"
}

export enum UserRole {
  ADMIN = "ADMIN",
  RECRUITER = "RECRUITER",
  HIRING_MANAGER = "HIRING_MANAGER",
  VIEWER = "VIEWER",
}

export enum SuperAdminRole {
  SUPER_ADMIN = "super_admin",
  SUPPORT = "support",
  MARKETING = "marketing",
  SALES = "sales",
  DEVELOPER = "developer",
}

export interface PricingPlan {
  id: string;
  plan_id: string;
  name: string;
  description?: string;
  tagline?: string;
  price_monthly: number;
  price_yearly?: number;
  currency: string;
  trial_days: number;
  is_trial_plan: boolean;
  features?: string[];
  max_users?: number;
  max_candidates?: number;
  max_jobs?: number;
  max_storage_mb?: number;
  is_popular: boolean;
  is_coming_soon: boolean;
  is_active: boolean;
  is_visible: boolean;
  // Feature flags for access control
  has_job_seeker_access?: boolean;
  has_job_posting?: boolean;
  has_ai_matching?: boolean;
  has_ai_search?: boolean;
  has_advanced_analytics?: boolean;
  has_api_access?: boolean;
  has_priority_support?: boolean;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  logo_url?: string;
  subscription_tier: SubscriptionTier;
  subscription_active: boolean;
  sales_contact_id?: string;
  monthly_revenue?: number;
  annual_contract_value?: number;
  customer_health_score?: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  total_candidates_processed?: number;
  total_ai_processing_cost?: number;
  pricing_plan?: PricingPlan;
}

export enum SubscriptionTier {
  FREE = "free",
  BASIC = "basic",
  PROFESSIONAL = "professional",
  PREMIUM = "premium",
  ENTERPRISE = "enterprise",
}

export interface Candidate {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  country_code?: string;
  phone?: string;
  alternate_email?: string;
  alternate_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  location?: string;
  professional_summary?: string;
  objective?: string;
  current_job_title?: string;
  current_company?: string;
  years_of_experience?: number;
  expected_salary?: number;
  salary_currency?: string;
  availability?: string;
  preferred_work_type?: string;
  willing_to_relocate?: boolean;
  work_authorization?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  twitter_url?: string;
  personal_website?: string;
  ai_match_score?: number;
  ai_career_level?: string;
  ai_summary?: string;
  ai_keywords?: string[];
  ai_skills_confidence?: Record<string, number>;
  ai_experience_summary?: string;
  ai_personality_traits?: string[];
  ai_sector_classifications?: SectorClassification[];
  ai_primary_sector?: string;
  contact_unlocked?: boolean;
  is_identity_verified?: boolean;
  is_id_verified?: boolean;

  // Enhanced matching fields for better job recommendations
  suitable_job_titles?: JobTitleSuggestion[];
  suitable_industries?: string[];
  career_progression_path?: string;
  transferable_skills?: string[];
  profile_tags?: string[];
  industry_experience?: Record<string, number>;
  company_sizes_worked?: string[];
  leadership_experience?: boolean;
  remote_work_experience?: boolean;
  international_experience?: boolean;
  resume_completeness_score?: number;
  resume_quality_score?: number;
  last_resume_update?: string;

  // Structured data fields
  languages?: LanguageSkill[];
  certifications?: Certification[];
  awards?: Award[];
  publications?: Publication[];
  patents?: Patent[];
  projects?: Project[];
  references?: Reference[];
  hobbies_interests?: string[];
  volunteer_experience?: VolunteerExperience[];

  company_id: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  source?: string;
  source_details?: Record<string, any>;
  full_name: string;
  total_years_experience?: number;
  skills?: CandidateSkill[];
  experiences?: WorkExperience[];
  education?: Education[];
  resumes?: Resume[];
  // Legacy field names for backward compatibility
  work_experience?: WorkExperience[];

  // Public profile fields
  public_profile_slug?: string;
  public_profile_enabled?: boolean;
  public_profile_url?: string;
}

export interface CandidateSkill {
  id: string;
  candidate_id: string;
  skill_name: string;
  proficiency_level: string;
  years_of_experience?: number;
  confidence_score?: number;
  context?: string;
}

export interface WorkExperience {
  id: string;
  candidate_id: string;
  job_title: string;
  company_name: string;
  department?: string;
  employment_type?: string;
  location?: string;
  is_remote?: boolean;
  start_date?: string;
  end_date?: string;
  is_current: boolean;
  duration_text?: string;
  duration_years?: number;
  description?: string;
  responsibilities?: string[];
  achievements?: string[];
  technologies_used?: string[];
  ai_confidence?: number;
  salary?: number;
}

export interface Education {
  id: string;
  candidate_id: string;
  institution_name: string;
  degree_type?: string;
  degree_name?: string;
  field_of_study?: string;
  major?: string;
  minor?: string;
  gpa?: number;
  gpa_scale?: string;
  start_date?: string;
  end_date?: string;
  graduation_year?: number;

  is_current?: boolean;
  honors?: string[];
  relevant_coursework?: string[];
  ai_confidence?: number;
  location?: string;
  description?: string;
  coursework?: string[];
  activities?: string[];
}

export interface Resume {
  id: string;
  candidate_id: string;
  file_path?: string;
  original_filename: string;
  file_size?: number;
  file_type?: string;
  mime_type?: string;
  status: string;
  ai_provider?: string;
  ai_model?: string;
  processing_time?: number;
  confidence_score?: number;
  ai_score?: number;
  processing_cost?: number;
  tokens_used?: number;
  cost?: number;
  error_message?: string;
  is_primary?: boolean;
  created_at: string;
  updated_at?: string;
}

export enum ResumeStatus {
  UPLOADED = "uploaded",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  DUPLICATE = "duplicate",
}

// Search and Filter Types
export interface CandidateSearchRequest {
  query?: string;
  skills?: string[];
  location?: string;
  experience_min?: number;
  experience_max?: number;
  job_title?: string;
  company?: string;
  education_level?: string;
  availability?: string;
  salary_min?: number;
  salary_max?: number;
  ai_score_min?: number;
  work_type?: string;
  language_list?: string;
  is_identity_verified?: boolean;
  page: number;
  page_size: number;
  sort_by: string;
  sort_order: "asc" | "desc";
  search_scope?: "company" | "marketplace";
  track_search?: boolean;
}

export interface CandidateSearchResponse {
  candidates: CandidateListItem[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface CandidateListItem {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  current_job_title?: string;
  current_company?: string;
  years_of_experience?: number;
  location?: string;
  ai_match_score?: number;
  ai_career_level?: string;
  created_at: string;
  latest_resume_status?: ResumeStatus;
  skills_count: number;
  experience_count: number;
  contact_unlocked: boolean;
  is_identity_verified?: boolean;
  is_id_verified?: boolean;
  skills?: Skill[];
}

type Skill = {
  skill_name: string;
  proficiency_level?: string;
};
// Candidate Search Filters
export interface CandidateSearchFilters {
  search?: string;
  skills?: string;
  location?: string;
  job_title?: string;
  experience_min?: number;
  experience_max?: number;
  salary_min?: number;
  salary_max?: number;
  education_level?: string;
  employment_type?: string;
  remote_preference?: string;
  availability?: string;
  languages?: string[];
  company?: string;
  is_identity_verified?: boolean;
}

// Job Description Smart Search
export interface JobDescriptionSearch {
  job_description: string;
  required_skills?: string[];
  preferred_skills?: string[];
  experience_level?: string;
  location?: string;
  salary_range?: {
    min: number;
    max: number;
  };
}

// Analytics Types
export interface CandidateAnalytics {
  total_candidates: number;
  new_candidates_this_month: number;
  top_skills: SkillAnalytics[];
  experience_distribution: ExperienceDistribution[];
  location_distribution: LocationDistribution[];
  sector_distribution: SectorDistribution[];
  ai_processing_stats: AIProcessingStats;
  search_analytics: SearchAnalytics;
  // Comprehensive data analytics
  top_certifications: CertificationAnalytics[];
  top_languages: LanguageAnalytics[];
  top_project_technologies: TechnologyAnalytics[];
  awards_distribution: AwardAnalytics[];
  candidates_with_projects: number;
  candidates_with_certifications: number;
  candidates_with_awards: number;
  candidates_with_languages: number;
  avg_projects_per_candidate: number;
  avg_certifications_per_candidate: number;
}

export interface SkillAnalytics {
  skill_name: string;
  candidate_count: number;
  avg_proficiency: number;
}

export interface CertificationAnalytics {
  certification_name: string;
  candidate_count: number;
}

export interface LanguageAnalytics {
  language_name: string;
  candidate_count: number;
}

export interface TechnologyAnalytics {
  technology_name: string;
  project_count: number;
}

export interface AwardAnalytics {
  award_name: string;
  candidate_count: number;
}

export interface ExperienceDistribution {
  experience_range: string;
  candidate_count: number;
  percentage: number;
}

export interface LocationDistribution {
  location: string;
  candidate_count: number;
  percentage: number;
}

export interface SectorDistribution {
  sector_name: string;
  candidate_count: number;
  percentage: number;
}

export interface AIProcessingStats {
  total_resumes_processed: number;
  avg_processing_time: number;
  avg_confidence_score: number;
  total_cost: number;
  success_rate: number;
  // Additional detailed stats for Upload Resumes page
  total_resumes?: number;
  processing_resumes?: number;
  failed_resumes?: number;
}

export interface SearchAnalytics {
  total_searches: number;
  avg_results_per_search: number;
  top_search_terms: SearchTerm[];
  search_trends: SearchTrend[];
}

export interface SearchTerm {
  term: string;
  search_count: number;
  avg_results: number;
}

export interface SearchTrend {
  date: string;
  search_count: number;
  unique_searchers: number;
}

// Candidate Profile Analytics (for candidates to see their visibility)
export interface CandidateProfileAnalytics {
  profile_views: number;
  search_appearances: number;
  search_keywords: SearchKeyword[];
  geographic_searches: GeographicSearch[];
  search_trends: CandidateSearchTrend[];
  profile_strength: ProfileStrength;
}

export interface SearchKeyword {
  keyword: string;
  appearance_count: number;
  click_through_rate: number;
}

export interface GeographicSearch {
  location: string;
  search_count: number;
  view_count: number;
}

export interface CandidateSearchTrend {
  date: string;
  views: number;
  searches: number;
}

export interface ProfileStrength {
  overall_score: number;
  completeness_score: number;
  keyword_optimization: number;
  experience_relevance: number;
  skill_demand: number;
  suggestions: string[];
}

// Authentication Types
export interface LoginRequest {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  phone?: string;
  job_title?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
  persona?: string;
}

// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

// File Upload Types
export interface FileUploadResponse {
  resume_id: string;
  status: ResumeStatus;
  processing_time: number;
  message: string;
}

export interface BulkUploadResponse {
  total_files: number;
  successful_uploads: number;
  failed_uploads: number;
  results: FileUploadResponse[];
}

// Super Admin Types
export interface SuperAdminStats {
  total_companies: number;
  total_users: number;
  total_candidates: number;
  total_resumes_processed: number;
  monthly_revenue: number;
  active_subscriptions: number;
  system_health: SystemHealth;
}

export interface SystemHealth {
  database_status: "healthy" | "warning" | "critical";
  api_response_time: number;
  storage_usage: number;
  error_rate: number;
}

// Form Types
export interface FormField {
  name: string;
  label: string;
  type:
    | "text"
    | "email"
    | "password"
    | "select"
    | "textarea"
    | "file"
    | "number";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

// Job Management Types
export interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: "full-time" | "part-time" | "contract" | "internship";
  remote: boolean;
  salary_min?: number;
  salary_max?: number;
  description: string;
  requirements: string[];
  skills: string[];
  experience_level: "entry" | "mid" | "senior" | "executive";
  status: "draft" | "active" | "paused" | "closed";
  applications_count: number;
  matches_count: number;
  created_at: string;
  updated_at: string;
}

export interface SectorClassification {
  sector_name: string;
  subsector?: string;
  confidence: number;
  reasoning: string;
  keywords_matched: string[];
}

// Enhanced matching and structured data interfaces
export interface JobTitleSuggestion {
  title: string;
  confidence: number;
  reasoning: string;
}

export interface LanguageSkill {
  language?: string; // Legacy field
  name?: string; // New field from backend
  proficiency: "beginner" | "intermediate" | "advanced" | "expert" | "native";
  proficiency_description?: string;
  is_native?: boolean;
  confidence?: number;
}

export interface Certification {
  name: string;
  issuing_organization: string;
  issue_date?: string;
  expiration_date?: string;
  credential_id?: string;
  credential_url?: string;
  does_not_expire?: boolean;
  confidence?: number;
}

export interface Award {
  title: string;
  organization: string;
  date?: string;
  description?: string;
  confidence?: number;
}

export interface Publication {
  title: string;
  authors?: string[];
  publication?: string;
  date?: string;
  url?: string;
  doi?: string;
  description?: string;
  confidence?: number;
}

export interface Patent {
  title: string;
  patent_number?: string;
  date?: string;
  description?: string;
  inventors?: string[];
  assignee?: string;
  confidence?: number;
}

export interface Project {
  name: string;
  description?: string;
  role?: string;
  technologies?: string[];
  url?: string;
  start_date?: string;
  end_date?: string;
  is_ongoing?: boolean;
  achievements?: string[];
  confidence?: number;
}

export interface Reference {
  name: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  relationship?: string;
  confidence?: number;
}

export interface VolunteerExperience {
  organization: string;
  role?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_current?: boolean;
  hours_per_week?: number;
  achievements?: string[];
  confidence?: number;
}

// Subscription and Billing Types

export enum BillingPeriod {
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  SEMI_ANNUAL = "semi_annual",
  YEARLY = "yearly",
  ONE_TIME = "one_time",
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  CANCELLED = "cancelled",
  PAST_DUE = "past_due",
  UNPAID = "unpaid",
  TRIALING = "trialing",
}

export enum PaymentStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  CANCELLED = "cancelled",
  REFUNDED = "refunded",
}

export interface Subscription {
  id: string;
  company_id: string;
  plan_id: string;
  status: SubscriptionStatus;
  billing_period: BillingPeriod;
  started_at: string;
  trial_ends_at?: string;
  current_period_start: string;
  current_period_end: string;
  cancelled_at?: string;
  ends_at?: string;
  current_users: number;
  current_jobs: number;
  current_candidates: number;
  current_storage_gb: number;
  monthly_resume_views: number;
  monthly_bulk_uploads: number;
  custom_max_users?: number;
  custom_max_resume_views?: number;
  custom_features?: Record<string, any>;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  created_at: string;
  updated_at: string;
  plan?: PricingPlan;
}

export interface SubscriptionCreateRequest {
  plan_id: string;
  billing_period: BillingPeriod;
  payment_method_id?: string;
  trial_period_days?: number;
}

export interface Payment {
  id: string;
  subscription_id: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  billing_period_start: string;
  billing_period_end: string;
  payment_method?: string;
  last4?: string;
  brand?: string;
  attempted_at: string;
  paid_at?: string;
  failed_at?: string;
  failure_reason?: string;
  receipt_url?: string;
  invoice_pdf_url?: string;
  tax_amount: number;
  tax_rate: number;
  created_at: string;
  updated_at: string;
}
